import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, unwrap } from "./client";
import type { components } from "./schema";

// Ranní kontrola (M3). Sdílení stavu mezi telefony pollingem (R-11): obrazovka kontroly každých 10 s.

type Schemas = components["schemas"];
export type CheckToday = Schemas["CheckTodayResponse"];
export type CheckRun = Schemas["CheckRunResponse"];
export type CheckItem = Schemas["CheckItemResponse"];
export type CheckAnswer = Schemas["CheckAnswerResponse"];
export type CheckResult = Schemas["CheckResult"];
export type CheckAction = Schemas["CheckAction"];
export type SaveAnswer = Schemas["SaveAnswerRequest"];
export type CheckHistoryEntry = Schemas["CheckHistoryEntry"];

export const checkPollInterval = 10_000;
const todayKey = ["checks", "today"] as const;
const runKey = (runId: string) => ["checks", "run", runId] as const;

export function useTodayChecks() {
  return useQuery({
    queryKey: todayKey,
    queryFn: async () => unwrap(await api.GET("/api/v1/checks/today")),
    refetchInterval: checkPollInterval,
  });
}

/** Otevře dnešní kontrolu skladu (nebo vrátí rozdělanou). */
export function useOpenCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (warehouseId: string) =>
      unwrap(await api.POST("/api/v1/checks/warehouses/{warehouseId}/open", { params: { path: { warehouseId } } })),
    onSuccess: (run) => {
      queryClient.setQueryData(runKey(run.id), run);
      void queryClient.invalidateQueries({ queryKey: todayKey });
    },
  });
}

export function useCheckRun(runId: string | undefined, poll = true) {
  return useQuery({
    queryKey: runKey(runId ?? ""),
    enabled: !!runId,
    queryFn: async () => unwrap(await api.GET("/api/v1/checks/runs/{runId}", { params: { path: { runId: runId! } } })),
    refetchInterval: poll ? checkPollInterval : false,
  });
}

/**
 * Uložení odpovědi s optimistickou aktualizací (NF-PER-01): karta se posune hned, odpověď se odešle na pozadí.
 * Výpadky sítě se opakují; odpovědi k téže položce jdou za sebou (scope), ID odpovědi generuje klient (R-03).
 */
export const saveAnswerKey = (runId: string) => ["check-answer", runId] as const;

export function useSaveAnswer(runId: string, answeredByName: string, answeredBy: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: saveAnswerKey(runId),
    mutationFn: async ({ itemId, body }: { itemId: string; body: SaveAnswer }) =>
      unwrap(await api.PUT("/api/v1/checks/runs/{runId}/answers/{itemId}", { params: { path: { runId, itemId } }, body })),
    retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    onMutate: async ({ itemId, body }) => {
      await queryClient.cancelQueries({ queryKey: runKey(runId) });
      const previous = queryClient.getQueryData<CheckRun>(runKey(runId));
      queryClient.setQueryData<CheckRun>(runKey(runId), (run) =>
        run && {
          ...run,
          items: run.items.map((item) =>
            item.itemId === itemId
              ? {
                  ...item,
                  answer: {
                    id: body.answerId,
                    result: body.result,
                    countedQty: body.result === "Ok" ? item.parLevel : body.countedQty,
                    action: body.result === "Low" && item.refillFromWarehouseId ? "Refill" : body.action,
                    buyQty: body.buyQty,
                    prepareDate: null,
                    moveFromWarehouseId: body.moveFromWarehouseId,
                    moveToWarehouseId: body.moveToWarehouseId,
                    moveQty: body.moveQty,
                    answeredBy,
                    answeredByName,
                    answeredAt: new Date().toISOString(),
                  },
                }
              : item,
          ),
        },
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(runKey(runId), context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: runKey(runId) });
      void queryClient.invalidateQueries({ queryKey: todayKey });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useCompleteCheck(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/checks/runs/{runId}/complete", { params: { path: { runId } } })),
    onSuccess: (run) => {
      queryClient.setQueryData(runKey(runId), run);
      void queryClient.invalidateQueries({ queryKey: todayKey });
      void queryClient.invalidateQueries({ queryKey: ["checks", "history"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useCheckHistory(warehouseId?: string) {
  return useQuery({
    queryKey: ["checks", "history", warehouseId ?? null],
    queryFn: async () => unwrap(await api.GET("/api/v1/checks/history", { params: { query: { warehouseId } } })),
  });
}
